import React from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import BasePage from '../../baseClasses/BasePage';
import aoLogo from '../../assets/images/ao-logo.svg';

import mobileBridge from '../../helpers/MobileBridge';
import dataModel from '../../models/DataModel';
import SessionManager from '../../helpers/session/SessionManager';
import SessionsService from '../../services/sessionsService';
import CacheUtil from '../../helpers/session/CacheUtil';
import ModalBannerConstants from '../../constants/ModalBannerConstants';

import './login.scss';
import Input from '../../components/Input';
import { userHasInsightsOnlyRole, INSIGHTS_DEFAULT_ROUTE } from '../../helpers/user/insightsRoleAccess';

const INSIGHTS_DASHBOARD_URL = process.env.REACT_APP_INSIGHTS_DASHBOARD_URL || '';

/**
 * When REACT_APP_INSIGHTS_DASHBOARD_URL is not set at build time (e.g. ao-qa.activeoversight.io build),
 * derive allowed Insights origin from current host so redirect still works:
 * ao-qa.activeoversight.io -> insights-qa.activeoversight.io, ao-staging... -> insights-staging..., etc.
 */
function getInsightsOriginFallback() {
  if (typeof window === 'undefined' || !window.location || !window.location.hostname) return null;
  const hostname = window.location.hostname;
  const match = hostname.match(/^ao-(qa|staging|dev)\.activeoversight\.io$/i);
  if (!match) return null;
  const env = match[1].toLowerCase();
  return `https://insights-${env}.activeoversight.io`;
}

/**
 * All origins that may receive post-login redirect (Insights).
 * - Baked REACT_APP_INSIGHTS_DASHBOARD_URL (e.g. production iframe URL in the bundle).
 * - Host-derived origin when on ao-qa|staging|dev.activeoversight.io so QA still works if the bundle
 *   was built with production Insights URL (insights.activeoversight.io) but redirect_uri is insights-qa.
 */
function getAllowedRedirectOrigins() {
  const origins = new Set();
  if (INSIGHTS_DASHBOARD_URL) {
    try {
      origins.add(new URL(INSIGHTS_DASHBOARD_URL).origin);
    } catch (error) {
      // ignore invalid env
    }
  }
  const fromHost = getInsightsOriginFallback();
  if (fromHost) {
    origins.add(fromHost);
  }
  return origins;
}

function isRedirectUriAllowed(redirectUri) {
  const allowed = getAllowedRedirectOrigins();
  if (!allowed.size || !redirectUri || typeof redirectUri !== 'string') return false;
  try {
    return allowed.has(new URL(redirectUri).origin);
  } catch (e) {
    return false;
  }
}

/**
 * MCP OAuth (AFB-348): the AO Doorkeeper /oauth/authorize endpoint redirects here
 * with ?return_to=<authorize_url>. Only allow returning to the AO API origin so this
 * can't be turned into an open redirect.
 */
function isOauthReturnToAllowed(returnTo) {
  if (!returnTo || typeof returnTo !== 'string') return false;
  let apiOrigin;
  try {
    apiOrigin = new URL(process.env.REACT_APP_API).origin;
  } catch (e) {
    return false;
  }
  try {
    return new URL(returnTo).origin === apiOrigin;
  } catch (e) {
    return false;
  }
}

export class Login extends BasePage {
  constructor(props) {
    super(props);
    this.state = {
      email: '',
      password: '',
      passwordFocus: false,
      emailFocus: false,
      showPassword: false,
      loginFailedMessage: null,
    };
    
    if(mobileBridge.isMobile){
      if((localStorage.getItem('offlineMode') && localStorage.getItem('offlineMode')!='true') || (localStorage.getItem('token')==null && localStorage.getItem('token')=='')){
        delete localStorage.token;
        delete localStorage.loggedin_user_id;
        delete localStorage.current_company_id;
        delete localStorage.primary_company_id;
      }
    }else if (!SessionManager.isLoginInProgress()) {
      // Don't wipe the shared token while another tab is mid-login -- landing
      // on this screen because a foreign session change bounced us here must
      // not delete the token that the other tab just wrote and is still using.
      delete localStorage.token;
      delete localStorage.loggedin_user_id;
      delete localStorage.current_company_id;
      delete localStorage.primary_company_id;
    }

    this.focusPassword = this.focusPassword.bind(this);
    this.blurPassword = this.blurPassword.bind(this);
    this.focusEmail = this.focusEmail.bind(this);
    this.blurEmail = this.blurEmail.bind(this);
    this.toggleShowPassword = this.toggleShowPassword.bind(this);
  }

  componentDidMount() {
    super.componentDidMount();
    if (mobileBridge.isMobile) {
      mobileBridge.initialize(this.props.history);
    }
    // Explain a forced sign-out triggered by a different account signing in
    // (or out) in another browser window/tab. The flag is per-window
    // (sessionStorage), set by SessionManager.handleForeignSessionChange.
    let foreignSessionSignout = false;
    try {
      foreignSessionSignout = sessionStorage.foreignSessionSignout === '1';
      if (foreignSessionSignout) {
        delete sessionStorage.foreignSessionSignout;
      }
    }
    catch (err) {
      // sessionStorage unavailable (e.g. private mode) -> just skip the notice.
    }
    if (foreignSessionSignout) {
      this.setState({
        loginFailedMessage: 'You were signed out. Please sign in again.',
      });
    }
  }

  focusPassword() {
    window.requestAnimationFrame(timestamp => {
      this.setState({ passwordFocus: true, loginFailedMessage: null });
    });
  }

  blurPassword() {
    if (this.state.password) {
    } else {
      window.requestAnimationFrame(timestamp => {
        this.setState({ passwordFocus: false });
      });
    }
  }

  focusEmail() {
    window.requestAnimationFrame(timestamp => {
      this.setState({ emailFocus: true, loginFailedMessage: null });
    });
  }

  blurEmail() {
    if (this.state.email) {
    } else {
      window.requestAnimationFrame(timestamp => {
        this.setState({ emailFocus: false });
      });
    }
  }

  handleEmailChange(event) {
    this.setState({ email: event.target.value });
  }

  handlePasswordChange(event) {
    this.setState({
      password: event.target.value,
      loginFailedMessage: null,
    });
  }

  handleKeyDown(event) {
    if (event.keyCode == 13 && this.state.email && this.state.password) {
      this.handleLogin();
    }
  }

  handleLogin() {
    const { email, password } = this.state;
    // TODO: should we be passing around a plaintext password?
    const self = this;
    const search = (this.props.location && this.props.location.search) || '';
    const redirectUri = search ? new URLSearchParams(search.slice(1)).get('redirect_uri') : null;
    const returnTo = search ? new URLSearchParams(search.slice(1)).get('return_to') : null;
    if (redirectUri && !isRedirectUriAllowed(redirectUri)) {
      console.warn(
        '[Login] redirect_uri present but not allowed. Insights redirect will not happen. ' +
        'redirect_uri origin must match REACT_APP_INSIGHTS_DASHBOARD_URL or (on ao-qa|staging|dev.activeoversight.io) the host-derived Insights origin.',
        { redirectUri, allowedOrigins: [...getAllowedRedirectOrigins()] }
      );
    }
    SessionManager.login(email, password, false)
      .then(loggedInUser => {
        if (!self.props.mobileBridge.isMobile) {
          // MCP OAuth (AFB-348): return_to is the AO /oauth/authorize URL. Mint a
          // single-use login_hint and bounce back so Doorkeeper can resolve the user.
          if (returnTo && isOauthReturnToAllowed(returnTo)) {
            SessionsService.mintOauthLoginCode().then(loginHint => {
              if (typeof loginHint !== 'string' || !loginHint) {
                self.setState({ loginFailedMessage: 'Could not complete sign-in. Please try again.' });
                return;
              }
              const sep = returnTo.indexOf('?') === -1 ? '?' : '&';
              window.location.href = returnTo + sep + 'login_hint=' + encodeURIComponent(loginHint);
            });
            return;
          }
          if (redirectUri && isRedirectUriAllowed(redirectUri)) {
            const token = localStorage.token || '';
            const companyId = localStorage.current_company_id || localStorage.primary_company_id || '';
            const base = redirectUri.split('#')[0];
            const hash = `#token=${encodeURIComponent(token)}&companyId=${encodeURIComponent(companyId)}`;
            window.location.href = base + hash;
            return;
          }
          this.gotoDefaultPlace(loggedInUser);
          // Insights-only users never see this announcement. It walks them to a Settings menu their
          // minimal header does not render, and closing it PATCHes /users/:id to record that it was
          // seen -- a route the API denies to that role. The failed write means the flag never
          // sticks, so without this guard the overlay returns on every single login.
          if (loggedInUser
              && !userHasInsightsOnlyRole(loggedInUser)
              && (!loggedInUser.settings || !loggedInUser.settings.has_viewed_web_notifications_announcement)) {
            dataModel.showCircleGlobalModal(ModalBannerConstants.NOTIFICATIONS_ANNOUNCEMENT_CIRCLE_ANNOUNCEMENT, null);
          }
        }
        else {
          self.props.history.replace('/mobile');
        }
        CacheUtil.reloadSpaIfNeeded();
      })
      .catch(loginError => {
        this.setState({ loginFailedMessage: loginError.message });
      });
  }

  gotoDefaultPlace(loggedInUser) {
    if (userHasInsightsOnlyRole(loggedInUser)) {
      this.props.history.replace(INSIGHTS_DEFAULT_ROUTE);
      return;
    }
    if (!loggedInUser.role_name || loggedInUser.role_name == 'standard_user') {
      this.props.history.replace('/tasks/project/all');
    }
    else {
      this.props.history.replace('/projects/summary');
    }
  }

  toggleShowPassword() {
    this.setState({ showPassword: !this.state.showPassword });
  }

  registerBridge(element) {}

  handleDismissMessage() {
    this.handledMessageDisplay = true;
  }

  render() {
    if (!this.props.mobileBridge) {
      return null;
    }

    let passwordInput = null;
    if (this.state.showPassword) {
      passwordInput = (
        <Input
          className="login-input mat-input"
          id="password"
          onKeyDown={e => this.handleKeyDown(e)}
          onFocus={this.focusPassword}
          onBlur={this.blurPassword}
          onChange={this.handlePasswordChange}
          type="text"
        />
      );
    } else {
      passwordInput = (
        <Input
          className="login-input mat-input"
          id="password"
          onKeyDown={e => this.handleKeyDown(e)}
          onFocus={this.focusPassword}
          onBlur={this.blurPassword}
          onChange={this.handlePasswordChange}
          type="password"
        />
      );
    }

    return (
      <div
        id="login-page"
        className="login-background"
        ref={element => {
          this.registerBridge(element);
        }}
      >
        <section className="login-form">
          <img className="login-logo" src={aoLogo} alt="logo" />
          <div className={this.state.emailFocus || this.state.email ? 'login-email mat-div is-active is-completed' : 'login-email mat-div'}>
            <label className="login-label mat-label" htmlFor="email">
              {this.state.emailFocus || this.state.email ? 'EMAIL' : 'ENTER YOUR EMAIL'}
            </label>
            <Input
              className="login-input mat-input"
              id="email"
              type="email"
              onKeyDown={e => this.handleKeyDown(e)}
              onFocus={this.focusEmail}
              onBlur={this.blurEmail}
              onChange={this.handleEmailChange}
            />
          </div>
          <div
            className={
              this.state.passwordFocus || this.state.password ? 'login-password mat-div is-active is-completed' : 'login-password mat-div'
            }
          >
            <label className="login-label mat-label" htmlFor="password">
              {this.state.passwordFocus || this.state.password ? 'PASSWORD' : 'ENTER YOUR PASSWORD'}
            </label>
            {passwordInput}
            <div
              className={this.state.showPassword ? 'show-password' : 'hide-password'}
              onClick={this.toggleShowPassword}
            />
          </div>
          <div className="login-message-container">
            <div
              className={`login-error-message ${
                (this.state.loginFailedMessage) ? 'login-error-message-visible' : 'login-error-message-hidden'
              }`}
              dangerouslySetInnerHTML={{ __html: this.state.loginFailedMessage }}/>
          </div>
          <button
            className={
              this.state.password && this.state.email && (!this.state.loginFailedMessage)
                ? 'login-button blue-btn'
                : 'login-button white-btn'
            }
            onClick={this.handleLogin}
          >
            LOG IN
          </button>
          <Link to="/forgot-password/">
            <div className="forgot-password" onClick={this.handleForgotPassword}>
              FORGOT PASSWORD?
            </div>
          </Link>
          <div id="buttons"> </div>
          <div id="log"> </div>
        </section>
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    dataModel: dataModel,
    mobileBridge: dataModel.mobileBridge,
    passthroughUri: state.main.passthroughUri,
    navigateUri: state.main.navigateUri,
  };
};
export default withRouter(connect(mapStateToProps)(Login));



// WEBPACK FOOTER //
// ./src/containers/Login/index.js